import React, { useState, useEffect, useCallback } from "react";
import { api } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, CornerDownRight, Send, ThumbsUp } from "lucide-react";

const Bubble = ({ c, onReply, onDelete, onLike }) => (
  <div data-testid={`comment-${c.id}`}>
    <div className="flex items-start gap-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-xs font-black text-white">{(c.author || "?").charAt(0).toUpperCase()}</span>
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl bg-gray-100 px-3 py-2">
          <div className="text-xs font-bold text-gray-800">{c.author}{c.rank ? <span className="ml-1 font-normal capitalize text-gray-400">· {c.rank}</span> : null}</div>
          <div className="text-sm text-gray-700">{c.text}</div>
        </div>
        <div className="mt-1 flex items-center gap-3 px-1 text-xs text-gray-400">
          <span>{c.time}</span>
          <button onClick={() => onLike(c)} data-testid={`comment-like-${c.id}`} className={`flex items-center gap-1 font-bold hover:text-[#002855] ${c.liked ? "text-[#002855]" : ""}`}>
            <ThumbsUp className="h-3 w-3" fill={c.liked ? "#002855" : "none"} /> {c.likes > 0 ? c.likes : "Like"}
          </button>
          <button onClick={() => onReply(c)} data-testid={`reply-btn-${c.id}`} className="font-bold hover:text-[#002855]">Reply</button>
          {c.is_mine && <button onClick={() => onDelete(c)} data-testid={`comment-delete-${c.id}`} className="font-bold hover:text-red-500">Delete</button>}
        </div>
      </div>
    </div>
  </div>
);

const CommentsSection = ({ postId, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await api.get(`/posts/${postId}/comments`)).data;
      setComments(data.comments);
      onCountChange?.(data.total);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [postId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    const body = text.trim();
    const parentId = replyTo?.id || null;
    setText(""); setReplyTo(null);
    try {
      await api.post(`/posts/${postId}/comments`, { text: body, parent_id: parentId });
      await load();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const del = async (c) => {
    try {
      await api.delete(`/comments/${c.id}`);
      await load();
    } catch { /* ignore */ }
  };

  const like = async (c) => {
    try {
      const res = (await api.post(`/comments/${c.id}/like`)).data;
      const apply = (x) => (x.id === c.id ? { ...x, liked: res.liked, likes: res.likes } : x);
      setComments((list) => list.map((r) => ({ ...apply(r), replies: (r.replies || []).map(apply) })));
    } catch { /* ignore */ }
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3" data-testid={`comments-${postId}`}>
      <div className="flex items-center gap-2">
        {replyTo && (
          <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
            <CornerDownRight className="h-3 w-3" /> Replying to {replyTo.author}
            <button onClick={() => setReplyTo(null)} className="ml-1 text-gray-400">✕</button>
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={replyTo ? "Write a reply…" : "Add a comment…"} className="h-10 rounded-full" data-testid={`comment-input-${postId}`} />
        <Button onClick={submit} disabled={sending || !text.trim()} data-testid={`comment-submit-${postId}`}
          className="h-10 w-10 shrink-0 rounded-full bg-[#002855] p-0 hover:bg-[#001d40]">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      <div className="mt-3 space-y-3">
        {loading && <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
        {!loading && comments.length === 0 && <p className="py-2 text-center text-xs text-gray-400">Be the first to comment.</p>}
        {comments.map((c) => (
          <div key={c.id}>
            <Bubble c={c} onReply={setReplyTo} onDelete={del} onLike={like} />
            {c.replies?.length > 0 && (
              <div className="ml-10 mt-2 space-y-2 border-l-2 border-gray-100 pl-3">
                {c.replies.map((r) => <Bubble key={r.id} c={r} onReply={setReplyTo} onDelete={del} onLike={like} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommentsSection;
